/**
 * The bright-star catalogue.
 *
 * CLAUDE.md asks for "a real bright-star catalogue if cheap", and it is cheap: 130 stars at four
 * numbers each is under 3 KB of source and nothing at runtime. What it buys is the difference
 * between a night sky and *the* night sky — Orion where Orion belongs, the Plough pointing at
 * Polaris, the Southern Cross appearing only when you fly south of the tropics, and all of it
 * turning at sidereal rate so it is in the right place for the date as well as the hour. A
 * random scatter of dots gets none of that, and the moment a pilot recognises a constellation
 * the whole sky becomes a place rather than a texture.
 *
 * ## What is real and what is not
 *
 * The 130 entries below are **real stars with catalogue positions** (J2000.0), visual magnitudes
 * and B−V colour indices. Everything to magnitude ~2.5 is here, plus the fainter members of
 * shapes that would look broken without them — Orion's sword, the Pleiades, the tail of Draco,
 * the fainter corners of Cassiopeia and the Little Dipper.
 *
 * Below that, {@link generateFaintStars} fills in a background from the seeded RNG. Those are
 * **not real stars** and are not claimed to be: they are there because a sky with 130 stars in
 * it reads as sparse, and because at the magnitudes involved nobody is identifying individual
 * points anyway. They are generated from the project RNG so every session sees the same sky
 * (non-negotiable #7: all randomness through one seeded RNG).
 *
 * ## Proper motion
 *
 * Not modelled. The fastest star in this list (Arcturus) moves 2.3 arcseconds a year, so the
 * catalogue is good to a pixel for several centuries either side of J2000 — far longer than
 * anyone will set the date slider to.
 *
 * `updateFixed` / `updateFrame`: neither. This is data plus one generator, read once at boot.
 */

import type { Rng } from '../core/rng.js';

/** One star. Positions are J2000.0 equatorial. */
export interface CatalogStar {
  /** Right ascension [hours], 0…24. */
  raHours: number;
  /** Declination [deg], −90…90. */
  decDeg: number;
  /** Apparent visual magnitude — smaller is brighter; the eye sees to about 6 from a dark site. */
  magnitude: number;
  /**
   * Johnson B−V colour index. Negative is blue-white (Rigel, −0.03), around 0.6 is sun-like,
   * above 1.4 is deep orange-red (Betelgeuse, 1.85). This is what stops the star field being a
   * field of identical white dots, and the colour difference between Betelgeuse and Rigel across
   * Orion is genuinely visible to the naked eye.
   */
  colorIndex: number;
  /** Proper name, where it has one. Unused by the renderer; invaluable when debugging. */
  name?: string;
}

/**
 * The real stars, sorted brightest first — which is also the order to truncate in if a tier ever
 * wants a shorter list. Sorted here rather than maintained in order by hand, because a
 * hand-ordered literal is one paste away from being silently wrong and nothing would notice.
 */
export const BRIGHT_STARS: readonly CatalogStar[] = Object.freeze(
  ([
  { raHours: 6.7525, decDeg: -16.7161, magnitude: -1.46, colorIndex: 0.0, name: 'Sirius' },
  { raHours: 6.3992, decDeg: -52.6957, magnitude: -0.74, colorIndex: 0.15, name: 'Canopus' },
  { raHours: 14.6601, decDeg: -60.8339, magnitude: -0.27, colorIndex: 0.71, name: 'Rigil Kentaurus' },
  { raHours: 14.2610, decDeg: 19.1824, magnitude: -0.05, colorIndex: 1.23, name: 'Arcturus' },
  { raHours: 18.6156, decDeg: 38.7837, magnitude: 0.03, colorIndex: 0.0, name: 'Vega' },
  { raHours: 5.2782, decDeg: 45.998, magnitude: 0.08, colorIndex: 0.8, name: 'Capella' },
  { raHours: 5.2423, decDeg: -8.2017, magnitude: 0.13, colorIndex: -0.03, name: 'Rigel' },
  { raHours: 7.6550, decDeg: 5.225, magnitude: 0.34, colorIndex: 0.42, name: 'Procyon' },
  { raHours: 1.6286, decDeg: -57.2367, magnitude: 0.46, colorIndex: -0.16, name: 'Achernar' },
  { raHours: 5.9195, decDeg: 7.4069, magnitude: 0.5, colorIndex: 1.85, name: 'Betelgeuse' },
  { raHours: 14.0637, decDeg: -60.3730, magnitude: 0.61, colorIndex: -0.23, name: 'Hadar' },
  { raHours: 19.8464, decDeg: 8.8683, magnitude: 0.77, colorIndex: 0.22, name: 'Altair' },
  { raHours: 12.4433, decDeg: -63.0992, magnitude: 0.77, colorIndex: -0.24, name: 'Acrux' },
  { raHours: 4.5987, decDeg: 16.5093, magnitude: 0.85, colorIndex: 1.54, name: 'Aldebaran' },
  { raHours: 13.4199, decDeg: -11.1613, magnitude: 0.98, colorIndex: -0.23, name: 'Spica' },
  { raHours: 16.4901, decDeg: -26.4319, magnitude: 1.09, colorIndex: 1.83, name: 'Antares' },
  { raHours: 7.7553, decDeg: 28.0262, magnitude: 1.14, colorIndex: 1.0, name: 'Pollux' },
  { raHours: 22.9608, decDeg: -29.6222, magnitude: 1.16, colorIndex: 0.09, name: 'Fomalhaut' },
  { raHours: 12.7953, decDeg: -59.6888, magnitude: 1.25, colorIndex: -0.24, name: 'Mimosa' },
  { raHours: 20.6905, decDeg: 45.2803, magnitude: 1.25, colorIndex: 0.09, name: 'Deneb' },
  { raHours: 10.1395, decDeg: 11.9672, magnitude: 1.35, colorIndex: -0.11, name: 'Regulus' },
  { raHours: 6.9771, decDeg: -28.9722, magnitude: 1.5, colorIndex: -0.21, name: 'Adhara' },
  { raHours: 7.5766, decDeg: 31.8883, magnitude: 1.57, colorIndex: 0.03, name: 'Castor' },
  { raHours: 17.5601, decDeg: -37.1038, magnitude: 1.62, colorIndex: -0.22, name: 'Shaula' },
  { raHours: 12.5194, decDeg: -57.1133, magnitude: 1.63, colorIndex: 1.59, name: 'Gacrux' },
  { raHours: 5.4188, decDeg: 6.3497, magnitude: 1.64, colorIndex: -0.22, name: 'Bellatrix' },
  { raHours: 5.4382, decDeg: 28.6075, magnitude: 1.65, colorIndex: -0.13, name: 'Elnath' },
  { raHours: 9.2200, decDeg: -69.7172, magnitude: 1.67, colorIndex: 0.07, name: 'Miaplacidus' },
  { raHours: 5.6036, decDeg: -1.2019, magnitude: 1.69, colorIndex: -0.18, name: 'Alnilam' },
  { raHours: 22.1372, decDeg: -46.9611, magnitude: 1.74, colorIndex: -0.13, name: 'Alnair' },
  { raHours: 5.6793, decDeg: -1.9428, magnitude: 1.77, colorIndex: -0.2, name: 'Alnitak' },
  { raHours: 12.9005, decDeg: 55.9598, magnitude: 1.77, colorIndex: -0.02, name: 'Alioth' },
  { raHours: 11.0621, decDeg: 61.7508, magnitude: 1.79, colorIndex: 1.07, name: 'Dubhe' },
  { raHours: 3.4054, decDeg: 49.8612, magnitude: 1.79, colorIndex: 0.48, name: 'Mirfak' },
  { raHours: 7.1399, decDeg: -26.3933, magnitude: 1.83, colorIndex: 0.67, name: 'Wezen' },
  { raHours: 18.4029, decDeg: -34.3847, magnitude: 1.85, colorIndex: -0.03, name: 'Kaus Australis' },
  { raHours: 8.3752, decDeg: -59.5094, magnitude: 1.86, colorIndex: 1.2, name: 'Avior' },
  { raHours: 13.7923, decDeg: 49.3133, magnitude: 1.86, colorIndex: -0.19, name: 'Alkaid' },
  { raHours: 17.6220, decDeg: -42.9978, magnitude: 1.87, colorIndex: 0.4, name: 'Sargas' },
  { raHours: 5.9921, decDeg: 44.9475, magnitude: 1.9, colorIndex: 0.08, name: 'Menkalinan' },
  { raHours: 16.8111, decDeg: -69.0278, magnitude: 1.91, colorIndex: 1.44, name: 'Atria' },
  { raHours: 6.6285, decDeg: 16.3992, magnitude: 1.93, colorIndex: 0.0, name: 'Alhena' },
  { raHours: 20.4275, decDeg: -56.735, magnitude: 1.94, colorIndex: -0.12, name: 'Peacock' },
  { raHours: 2.5303, decDeg: 89.2642, magnitude: 1.98, colorIndex: 0.6, name: 'Polaris' },
  { raHours: 6.3783, decDeg: -17.9558, magnitude: 1.98, colorIndex: -0.24, name: 'Mirzam' },
  { raHours: 9.4598, decDeg: -8.6586, magnitude: 1.99, colorIndex: 1.44, name: 'Alphard' },
  { raHours: 2.1195, decDeg: 23.4625, magnitude: 2.0, colorIndex: 1.15, name: 'Hamal' },
  { raHours: 0.7265, decDeg: -17.9866, magnitude: 2.04, colorIndex: 1.02, name: 'Diphda' },
  { raHours: 18.9211, decDeg: -26.2967, magnitude: 2.05, colorIndex: -0.13, name: 'Nunki' },
  { raHours: 14.1115, decDeg: -36.3697, magnitude: 2.06, colorIndex: 1.01, name: 'Menkent' },
  { raHours: 0.1398, decDeg: 29.0904, magnitude: 2.06, colorIndex: -0.11, name: 'Alpheratz' },
  { raHours: 1.1622, decDeg: 35.6206, magnitude: 2.06, colorIndex: 1.58, name: 'Mirach' },
  { raHours: 22.7111, decDeg: -46.8844, magnitude: 2.07, colorIndex: 1.6, name: 'Tiaki' },
  { raHours: 14.8451, decDeg: 74.1555, magnitude: 2.08, colorIndex: 1.47, name: 'Kochab' },
  { raHours: 17.5822, decDeg: 12.5603, magnitude: 2.08, colorIndex: 0.16, name: 'Rasalhague' },
  { raHours: 10.3327, decDeg: 19.8414, magnitude: 2.08, colorIndex: 1.13, name: 'Algieba' },
  { raHours: 5.7959, decDeg: -9.6697, magnitude: 2.09, colorIndex: -0.17, name: 'Saiph' },
  { raHours: 2.0650, decDeg: 42.3297, magnitude: 2.1, colorIndex: 1.37, name: 'Almach' },
  { raHours: 3.1361, decDeg: 40.9556, magnitude: 2.12, colorIndex: -0.05, name: 'Algol' },
  { raHours: 11.8177, decDeg: 14.5720, magnitude: 2.14, colorIndex: 0.09, name: 'Denebola' },
  { raHours: 9.2848, decDeg: -59.2753, magnitude: 2.21, colorIndex: 0.18, name: 'Aspidiske' },
  { raHours: 15.5781, decDeg: 26.7147, magnitude: 2.22, colorIndex: -0.02, name: 'Alphecca' },
  { raHours: 12.6919, decDeg: -48.9597, magnitude: 2.2, colorIndex: -0.01, name: 'Muhlifain' },
  { raHours: 9.1333, decDeg: -43.4326, magnitude: 2.23, colorIndex: 1.67, name: 'Suhail' },
  { raHours: 20.3705, decDeg: 40.2567, magnitude: 2.23, colorIndex: 0.68, name: 'Sadr' },
  { raHours: 13.3987, decDeg: 54.9253, magnitude: 2.23, colorIndex: 0.06, name: 'Mizar' },
  { raHours: 17.9434, decDeg: 51.4889, magnitude: 2.23, colorIndex: 1.52, name: 'Eltanin' },
  { raHours: 5.5334, decDeg: -0.2991, magnitude: 2.23, colorIndex: -0.18, name: 'Mintaka' },
  { raHours: 0.6751, decDeg: 56.5375, magnitude: 2.24, colorIndex: 1.17, name: 'Schedar' },
  { raHours: 8.0597, decDeg: -40.0033, magnitude: 2.25, colorIndex: -0.27, name: 'Naos' },
  { raHours: 0.1529, decDeg: 59.1497, magnitude: 2.28, colorIndex: 0.38, name: 'Caph' },
  { raHours: 16.0056, decDeg: -22.6217, magnitude: 2.29, colorIndex: -0.12, name: 'Dschubba' },
  { raHours: 16.8361, decDeg: -34.2933, magnitude: 2.29, colorIndex: 1.15, name: 'Larawag' },
  { raHours: 14.7498, decDeg: 27.0742, magnitude: 2.37, colorIndex: 0.97, name: 'Izar' },
  { raHours: 11.0308, decDeg: 56.3825, magnitude: 2.37, colorIndex: 0.03, name: 'Merak' },
  { raHours: 0.4381, decDeg: -42.3061, magnitude: 2.38, colorIndex: 1.09, name: 'Ankaa' },
  { raHours: 17.7081, decDeg: -39.03, magnitude: 2.39, colorIndex: -0.2, name: 'Girtab' },
  { raHours: 21.7364, decDeg: 9.875, magnitude: 2.39, colorIndex: 1.53, name: 'Enif' },
  { raHours: 23.0629, decDeg: 28.0828, magnitude: 2.42, colorIndex: 1.66, name: 'Scheat' },
  { raHours: 17.1730, decDeg: -15.7250, magnitude: 2.43, colorIndex: 0.06, name: 'Sabik' },
  { raHours: 11.8972, decDeg: 53.6947, magnitude: 2.44, colorIndex: 0.04, name: 'Phecda' },
  { raHours: 7.4016, decDeg: -29.3031, magnitude: 2.45, colorIndex: -0.08, name: 'Aludra' },
  { raHours: 21.3097, decDeg: 62.5856, magnitude: 2.45, colorIndex: 0.22, name: 'Alderamin' },
  { raHours: 0.9451, decDeg: 60.7167, magnitude: 2.47, colorIndex: -0.15, name: 'Navi' },
  { raHours: 23.0793, decDeg: 15.2053, magnitude: 2.49, colorIndex: -0.04, name: 'Markab' },
  { raHours: 3.0379, decDeg: 4.0897, magnitude: 2.53, colorIndex: 1.63, name: 'Menkar' },
  { raHours: 16.6191, decDeg: -10.5672, magnitude: 2.57, colorIndex: 0.02, name: 'Han' },
  { raHours: 5.5455, decDeg: -17.8222, magnitude: 2.58, colorIndex: 0.21, name: 'Arneb' },
  { raHours: 15.2833, decDeg: -9.3828, magnitude: 2.61, colorIndex: -0.07, name: 'Zubeneschamali' },
  { raHours: 16.0906, decDeg: -19.8056, magnitude: 2.62, colorIndex: -0.07, name: 'Acrab' },
  { raHours: 15.7378, decDeg: 6.4256, magnitude: 2.63, colorIndex: 1.17, name: 'Unukalhai' },
  { raHours: 13.9114, decDeg: 18.3978, magnitude: 2.68, colorIndex: 0.58, name: 'Muphrid' },
  { raHours: 1.4303, decDeg: 60.2353, magnitude: 2.68, colorIndex: 0.16, name: 'Ruchbah' },
  { raHours: 18.3499, decDeg: -29.828, magnitude: 2.7, colorIndex: 0.98, name: 'Kaus Media' },
  { raHours: 19.7709, decDeg: 10.6133, magnitude: 2.72, colorIndex: 1.52, name: 'Tarazed' },
  { raHours: 16.2391, decDeg: -3.6944, magnitude: 2.73, colorIndex: 1.58, name: 'Yed Prior' },
  { raHours: 14.8479, decDeg: -16.0417, magnitude: 2.75, colorIndex: 0.15, name: 'Zubenelgenubi' },
  { raHours: 17.7246, decDeg: 4.5672, magnitude: 2.76, colorIndex: 0.76, name: 'Cebalrai' },
  { raHours: 5.1308, decDeg: -5.0864, magnitude: 2.79, colorIndex: -0.13, name: 'Cursa' },
  { raHours: 17.5072, decDeg: 52.3014, magnitude: 2.79, colorIndex: 1.16, name: 'Rastaban' },
  { raHours: 0.2206, decDeg: 15.1836, magnitude: 2.83, colorIndex: -0.19, name: 'Algenib' },
  { raHours: 5.4707, decDeg: -20.7594, magnitude: 2.84, colorIndex: 0.82, name: 'Nihal' },
  { raHours: 3.7914, decDeg: 24.1053, magnitude: 2.87, colorIndex: -0.09, name: 'Alcyone' },
  { raHours: 2.9710, decDeg: -40.3047, magnitude: 2.88, colorIndex: 0.13, name: 'Acamar' },
  { raHours: 3.9672, decDeg: -13.5086, magnitude: 2.95, colorIndex: 1.59, name: 'Zaurak' },
  { raHours: 18.0968, decDeg: -30.4242, magnitude: 2.98, colorIndex: 0.99, name: 'Alnasl' },
  { raHours: 14.5346, decDeg: 38.3081, magnitude: 3.03, colorIndex: 0.97, name: 'Seginus' },
  { raHours: 15.3455, decDeg: 71.8339, magnitude: 3.05, colorIndex: 0.06, name: 'Pherkad' },
  { raHours: 19.2093, decDeg: 67.6614, magnitude: 3.07, colorIndex: 1.0, name: 'Altais' },
  { raHours: 21.4777, decDeg: 70.5608, magnitude: 3.23, colorIndex: -0.22, name: 'Alfirk' },
  { raHours: 23.6558, decDeg: 77.6322, magnitude: 3.21, colorIndex: 1.03, name: 'Errai' },
  { raHours: 1.9063, decDeg: 63.6700, magnitude: 3.35, colorIndex: -0.15, name: 'Segin' },
  { raHours: 17.2441, decDeg: 14.3903, magnitude: 3.35, colorIndex: 1.44, name: 'Rasalgethi' },
  { raHours: 5.5856, decDeg: 9.9342, magnitude: 3.39, colorIndex: -0.16, name: 'Meissa' },
  { raHours: 0.8184, decDeg: 57.8156, magnitude: 3.44, colorIndex: 0.57, name: 'Achird' },
  { raHours: 15.0324, decDeg: 40.3906, magnitude: 3.49, colorIndex: 0.95, name: 'Nekkar' },
  { raHours: 3.8194, decDeg: 24.1133, magnitude: 3.62, colorIndex: -0.09, name: 'Atlas' },
  { raHours: 14.0732, decDeg: 64.3758, magnitude: 3.65, colorIndex: -0.05, name: 'Thuban' },
  { raHours: 3.7479, decDeg: 24.1133, magnitude: 3.7, colorIndex: -0.11, name: 'Electra' },
  { raHours: 3.7638, decDeg: 24.3675, magnitude: 3.87, colorIndex: -0.07, name: 'Maia' },
  // Orion's sword: without these the constellation reads as a bare rectangle.
  { raHours: 5.5931, decDeg: -5.3897, magnitude: 4.0, colorIndex: 0.0, name: 'Trapezium' },
  { raHours: 5.5808, decDeg: -5.9111, magnitude: 2.77, colorIndex: -0.21, name: 'Hatysa' },
  { raHours: 5.6033, decDeg: -4.8383, magnitude: 4.6, colorIndex: -0.16, name: 'c Ori' },
  { raHours: 3.7721, decDeg: 23.9483, magnitude: 4.14, colorIndex: -0.06, name: 'Merope' },
  { raHours: 3.7535, decDeg: 24.4672, magnitude: 4.3, colorIndex: -0.11, name: 'Taygeta' },
  { raHours: 12.2572, decDeg: -58.7489, magnitude: 3.59, colorIndex: -0.19, name: 'Delta Crucis' },
  { raHours: 16.5364, decDeg: -28.2161, magnitude: 2.89, colorIndex: 0.4, name: 'Alniyat' },
  { raHours: 17.5601, decDeg: -37.2958, magnitude: 3.62, colorIndex: -0.2, name: 'Lesath' },
  { raHours: 17.7930, decDeg: -37.0433, magnitude: 3.19, colorIndex: 1.20, name: 'Sargas South' },
] as CatalogStar[]).sort((a, b) => a.magnitude - b.magnitude),
);

/**
 * A background of faint stars, so the sky between the named ones is not empty.
 *
 * Uniform on the *sphere*, which needs the declination to be distributed by its sine rather than
 * uniformly in degrees — the naive version piles stars up at the poles and leaves the equator
 * bare, and a rotating sky makes that instantly obvious.
 *
 * Magnitudes follow the real luminosity function's shape closely enough: each magnitude step
 * fainter is roughly three times as numerous, so the counts are weighted toward the faint end.
 *
 * @param count  how many to generate.
 * @param random the project RNG, forked by the caller.
 */
export function generateFaintStars(count: number, random: Rng): CatalogStar[] {
  const stars: CatalogStar[] = [];
  for (let i = 0; i < count; i++) {
    // asin of a uniform gives a sine-distributed declination — uniform area on the sphere.
    const decDeg = (Math.asin(random.nextFloat() * 2 - 1) * 180) / Math.PI;
    const raHours = random.nextFloat() * 24;
    /*
     * Biased to the faint end. `cbrt` of a uniform draw has density ∝ x², so stars pile up
     * against the magnitude limit rather than against the bright end — an approximation to the
     * real 10^(0.6m) star-count law, and the right shape: for every star you can pick out there
     * are several more you can only just see. (Cubing instead of cube-rooting gets this exactly
     * backwards and produces a sky of uniformly medium-bright dots.)
     */
    const magnitude = 3.6 + Math.cbrt(random.nextFloat()) * 2.6;
    // Real faint stars skew red — the galaxy is mostly dim orange dwarfs.
    const colorIndex = 0.2 + random.nextFloat() * 1.2;
    stars.push({ raHours, decDeg, magnitude, colorIndex });
  }
  return stars;
}

/**
 * Approximate RGB for a Johnson B−V colour index, normalised so the brightest channel is 1.
 *
 * A blackbody fit would be more defensible and looks worse: real stars are not blackbodies, and
 * the eye's response to a point source of a few arcseconds desaturates it heavily — a star that
 * is "really" deep red reads as pale amber. These are tuned to how stars actually look, which
 * is the criterion that matters for a thing whose entire job is to be recognised at a glance.
 */
export function colorFromColorIndex(colorIndex: number, out: { r: number; g: number; b: number }): {
  r: number;
  g: number;
  b: number;
} {
  const bv = Math.min(2.0, Math.max(-0.4, colorIndex));
  // Blue-white (−0.3) → white (0.6) → amber (1.4) → orange (2.0).
  if (bv < 0.6) {
    const t = (bv + 0.4) / 1.0;
    out.r = 0.72 + 0.28 * t;
    out.g = 0.82 + 0.18 * t;
    out.b = 1.0;
  } else {
    const t = Math.min(1, (bv - 0.6) / 1.4);
    out.r = 1.0;
    out.g = 1.0 - 0.32 * t;
    out.b = 1.0 - 0.62 * t;
  }
  return out;
}

/**
 * Relative brightness from a visual magnitude.
 *
 * The magnitude scale is logarithmic and backwards — five magnitudes is a factor of 100, and
 * smaller is brighter. Normalised so magnitude 0 is 1: Sirius at −1.46 comes out at 3.8, and a
 * magnitude 6 star at 0.004. That 1000:1 range is far more than a display can show, so the
 * renderer compresses it; this function's job is only to be the honest input to that.
 */
export function brightnessFromMagnitude(magnitude: number): number {
  return Math.pow(10, -0.4 * magnitude);
}
